import { Box, Link, Typography } from '@mui/material';
import { ArrowLeftIcon } from 'lucide-react';
import { useState } from 'react';
import { SsbCharacteristic } from '../../types/SimilarlySituated/SsbCharacteristic.type';
import { SsbCondition } from '../../types/SimilarlySituated/SsbCondition.type';
import { SsbEntryPoint } from '../../types/enums/SsbEntryPoint.enum';
import { isSameCondition } from '../../utils/ssbBuilderBox';
import { findCharacteristic } from '../../utils/ssbCatalogMetadata';
import { conditionLabel, createCondition } from '../../utils/ssbCondition';
import { entryPointColors } from '../../utils/ssbConditionColors';
import AvailabilityCatalog from './AvailabilityCatalog';
import ConditionComposer from './ConditionComposer';

interface ConditionPanelProps {
  entryPoint: SsbEntryPoint;
  countyId?: number | null;
  editingCondition?: SsbCondition | null;
  conflicts?: { conditions: SsbCondition[]; message: string }[];
  onCommit: (condition: SsbCondition) => void;
  onClose: () => void;
}

function ConditionPanel({ entryPoint, countyId, editingCondition, conflicts, onCommit, onClose }: ConditionPanelProps) {
  const [draft, setDraft] = useState<SsbCondition | null>(editingCondition ?? null);

  const colors = entryPointColors(entryPoint);
  const characteristic = draft ? findCharacteristic(draft.characteristicId) : undefined;
  const isEditingExisting = Boolean(editingCondition && draft?.id === editingCondition.id);
  const isComposing = Boolean(draft && characteristic);
  const conflictMessage = draft
    ? conflicts?.find(({ conditions }) =>
        conditions.some((condition) => condition.id !== draft.id && isSameCondition(condition, draft)),
      )?.message
    : undefined;

  const handleCharacteristicSelect = (selected: SsbCharacteristic) => {
    const fresh = createCondition(selected);
    // re-select from the catalog while editing a chip keeps the chips identity
    setDraft(editingCondition ? { ...fresh, id: editingCondition.id } : fresh);
  };

  const handleCommit = (condition: SsbCondition) => {
    onCommit(condition);

    if (editingCondition && condition.id === editingCondition.id) {
      onClose();
      return;
    }
    // adding condition rests in the catalog with the same scope
    setDraft(null);
  };

  return (
    <Box
      sx={{
        border: `1px solid ${colors.border}`,
        borderRadius: '8px',
        backgroundColor: colors.panelBackground,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          padding: '12px 16px 4px',
        }}
      >
        {draft && characteristic ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px', minWidth: 0 }}>
            <Link
              component='button'
              type='button'
              underline='none'
              onClick={() => setDraft(null)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
                fontWeight: 600,
                color: colors.accent,
                flexShrink: 0,
                transition: 'opacity 0.2s ease-in-out',
                ':hover': {
                  opacity: 0.8,
                },
              }}
            >
              <ArrowLeftIcon size={14} />
              Back to catalog
            </Link>
            <Typography sx={{ fontSize: '12px' }}>{conditionLabel(draft)}</Typography>
          </Box>
        ) : (
          <Typography sx={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.08em', color: '#4A5568' }}>
            AVAILABILITY CATALOG
          </Typography>
        )}

        {/* the composer closes through its own Cancel and commit buttons */}
        {!isComposing && (
          <Link
            component='button'
            type='button'
            underline='none'
            onClick={onClose}
            sx={{
              color: '#718096',
              fontSize: '12px',
              fontWeight: 500,
              transition: 'color 0.2s ease-in-out',
              ':hover': { color: 'primary.main' },
            }}
          >
            Done
          </Link>
        )}
      </Box>

      {draft && characteristic ? (
        <Box sx={{ padding: '16px' }}>
          <ConditionComposer
            key={`${draft.id}-${draft.characteristicId}`}
            characteristic={characteristic}
            condition={draft}
            countyId={countyId}
            isEditing={isEditingExisting}
            errorMessage={conflictMessage}
            onDraftChange={setDraft}
            onCancel={() => (isEditingExisting ? onClose() : setDraft(null))}
            onCommit={handleCommit}
          />
        </Box>
      ) : (
        <AvailabilityCatalog entryPoint={entryPoint} countyId={countyId} onSelect={handleCharacteristicSelect} />
      )}
    </Box>
  );
}

export default ConditionPanel;
